import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  backButton: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  actionSection: {
    marginBottom: theme.spacing(4),
  },
  actionButton: {
    margin: theme.spacing(0, 1),
    padding: theme.spacing(1.5, 3),
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: theme.spacing(1),
  },
  archiveButton: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  closeButton: {
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  disabledButton: {
    backgroundColor: '#e0e0e0',
    color: '#9e9e9e',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  // Second Section - Summary Cards
  summarySection: {
    marginBottom: theme.spacing(4),
  },
  summaryGrid: {
    spacing: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    height: '100px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  interactiveCard: {
    cursor: 'pointer',
    backgroundColor: '#e3f2fd',
    '&:hover': {
      backgroundColor: '#bbdefb',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  locationCardSelected: {
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '& $summaryValue': {
      color: 'white',
    },
    '& $summaryLabel': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  locationCardUnselected: {
    cursor: 'pointer',
    backgroundColor: '#e0e0e0',
    color: '#9e9e9e',
    '& $summaryValue': {
      color: '#9e9e9e',
    },
    '& $summaryLabel': {
      color: '#bdbdbd',
    },
    '&:hover': {
      backgroundColor: '#d5d5d5',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  locationCardSmall: {
    height: '70px !important',
    padding: `${theme.spacing(1.5)}px !important`,
    '& $summaryValue': {
      fontSize: '1.1rem !important',
      marginBottom: theme.spacing(0.25),
    },
    '& $summaryLabel': {
      fontSize: '0.75rem !important',
    },
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.5),
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  // Third Section - Details and Items
  detailsSection: {
    marginBottom: theme.spacing(4),
  },
  detailsContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  leftDetails: {
    flex: '1 1 33%',
    [theme.breakpoints.down('md')]: {
      flex: '1 1 100%',
    },
  },
  rightItems: {
    flex: '1 1 67%',
    [theme.breakpoints.down('md')]: {
      flex: '1 1 100%',
    },
  },
  detailsCard: {
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  detailItem: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  detailLabel: {
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  itemsTable: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-head': {
      color: 'white',
      fontWeight: 'bold',
    },
  },
  // Status indicators
  statusChip: {
    fontWeight: 'bold',
    padding: theme.spacing(0.5, 1.5),
  },
  activeStatus: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  archivedStatus: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  closedStatus: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  // Loading states
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  errorContainer: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.error.main,
  },
  // Metrics Modal
  metricsModalPaper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    maxHeight: '85vh',
    overflowY: 'auto',
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      width: '95%',
    },
  },
  metricsModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  metricsModalFilter: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
  },
  metricsModalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

export default useStyles;